import { CUSTOMER_LEVEL } from '@wyz/types';
import { Card, Col, Row, Title } from '@wyz/ui';
import { useOnSuccessQuery } from '@wyz/utils';
import { useEffect, useEffectEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TextField } from '~/common/components';
import { Trans } from '~/common/context';
import {
  useManageCountries,
  useManageEcotaxesZones,
  useManageVatZones,
} from '~/connect/api';
import {
  SelectCountries,
  SelectEcotaxZones,
  SelectVatZones,
} from '~/connect/components';
import { CustomerUtils } from '~/connect/helpers';
import {
  CustomerFormProps,
  useCustomerFormLevel,
  useCustomerFormParentCustomer,
  useCustomerFormPrivileges,
} from '~/connect/hooks';
import type { ICustomerDeliveryInformationForm } from '~/connect/types';

const DeliveryBillingForm = ({ forms, customer }: CustomerFormProps) => {
  const methods = useFormContext<ICustomerDeliveryInformationForm>();

  const watchedLevel = useCustomerFormLevel({
    parent: forms[CustomerUtils.GENERAL_FORM_STEP].methods.getValues('parent'),
  });
  const FormPrivileges = useCustomerFormPrivileges({
    customer: customer,
    level: watchedLevel,
  });
  const parentCustomerQuery = useCustomerFormParentCustomer({
    methods: forms[CustomerUtils.GENERAL_FORM_STEP].methods,
  });
  const parentCustomer = parentCustomerQuery.data?.data;

  const watchedCountryLocalId = useWatch({
    name: 'deliveryInformation',
    control: methods.control,
  })?.country?.id;
  const watchedVatZoneId = useWatch({
    name: 'billingInformation',
    control: methods.control,
  })?.vatZone;
  const watchedEcoTaxZoneId = useWatch({
    name: 'billingInformation',
    control: methods.control,
  })?.ecotaxZone;

  const manageCountries = useManageCountries({
    listActive: { enabled: true },
  });

  const watchedCountry = manageCountries.listActive.data?.data.find(
    (item) => item.localId === watchedCountryLocalId,
  );
  const ecotaxesZones = useManageEcotaxesZones({
    getAll: { args: { countryId: Number(watchedCountry?.externalId) } },
  });

  const vatZones = useManageVatZones({
    getAll: { args: { countryId: Number(watchedCountry?.externalId) } },
  });

  const updateLevel = useEffectEvent((level: number) => {
    methods.setValue('billingInformation._level', level);
  });
  // Sync computed level to form field
  useEffect(() => {
    updateLevel(watchedLevel);
  }, [watchedLevel]);

  const setDefaultBillingZone = useEffectEvent((zoneId: number) => {
    methods.setValue('billingInformation.vatZone', Number(zoneId));
  });
  // Setting vat zone field from Parent if level 2+ or first if it's the only
  useEffect(() => {
    // if already set => no need to guess
    if (watchedVatZoneId) {
      return;
    }
    if (vatZones.data?.data.length === 1) {
      setDefaultBillingZone(Number(vatZones.data?.data[0].id));
    }
  }, [
    parentCustomer?.billingInformation.vatZone,
    vatZones.data?.data,
    watchedLevel,
    watchedVatZoneId,
  ]);

  // set first ecotax zone if we have no other choice
  useOnSuccessQuery(ecotaxesZones, (data) => {
    // if already set => no need to guess
    if (watchedEcoTaxZoneId) {
      return;
    }
    if (data.data.length === 1) {
      methods.setValue(
        'billingInformation.ecotaxZone',
        Number(data.data[0].id),
      );
    }
  });

  const updateIsoCode = useEffectEvent((isoCode: string) => {
    methods.setValue('deliveryInformation.country.isoCode', isoCode);
  });
  // sync country isoCode when country changes
  useEffect(() => {
    updateIsoCode(watchedCountry?.isoCode ?? '');
  }, [watchedCountry]);

  return (
    <Card>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Trans i18nKey='app.entity.tab.delivery' />
        </Title>
      </Card.Header>
      <Card.Body>
        <div>
          <Title heading='lg' weight='semi-bold' color='primary'>
            <Trans i18nKey='app.entity.form.label.delivery' />
          </Title>

          <Row>
            {FormPrivileges.DELIVERY_FORM.fields.ADDRESS.visible && (
              <Col sm={4}>
                <TextField
                  name='deliveryInformation.address'
                  label={
                    <Trans i18nKey='app.entity.form.delivery_information.address' />
                  }
                  required
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.ADDRESS.writable
                  }
                />
              </Col>
            )}
            {FormPrivileges.DELIVERY_FORM.fields.ADDITIONAL_ADDRESS.visible && (
              <Col sm={4}>
                <TextField
                  name='deliveryInformation.additionalAddress'
                  label={
                    <Trans i18nKey='app.entity.form.delivery_information.additional_address' />
                  }
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.ADDITIONAL_ADDRESS
                      .writable
                  }
                />
              </Col>
            )}

            {FormPrivileges.DELIVERY_FORM.fields.ZIP_CODE.visible && (
              <Col sm={4}>
                <TextField
                  name='deliveryInformation.zipCode'
                  label={<Trans i18nKey='app.common.customer.zip_code' />}
                  required
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.ZIP_CODE.writable
                  }
                />
              </Col>
            )}
            {FormPrivileges.DELIVERY_FORM.fields.CITY.visible && (
              <Col sm={4}>
                <TextField
                  name='deliveryInformation.city'
                  label={<Trans i18nKey='app.common.customer.city' />}
                  required
                  readOnly={!FormPrivileges.DELIVERY_FORM.fields.CITY.writable}
                />
              </Col>
            )}

            {FormPrivileges.DELIVERY_FORM.fields.COUNTRY.visible && (
              <Col sm={4}>
                <SelectCountries
                  name={'deliveryInformation.country.id'}
                  label={<Trans i18nKey='app.common.country' />}
                  required
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.COUNTRY.writable
                  }
                />
              </Col>
            )}
          </Row>
        </div>

        <div>
          <Title heading='lg' weight='semi-bold' color='primary'>
            <Trans i18nKey='app.entity.detail.label.billing' />
          </Title>
          <Row>
            {FormPrivileges.DELIVERY_FORM.fields.ECOTAX_ZONE.visible && (
              <Col sm={4}>
                <SelectEcotaxZones
                  name='billingInformation.ecotaxZone'
                  label={
                    <Trans i18nKey='app.entity.form.billing_information.ecotax_zone' />
                  }
                  countryId={Number(watchedCountry?.externalId)}
                  isClearable
                  required
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.ECOTAX_ZONE.writable
                  }
                />
              </Col>
            )}
            {FormPrivileges.DELIVERY_FORM.fields.VAT_ZONE.visible && (
              <Col sm={4}>
                <SelectVatZones
                  name='billingInformation.vatZone'
                  label={
                    <Trans i18nKey='app.entity.form.billing_information.vat_zone' />
                  }
                  countryId={Number(watchedCountry?.externalId)}
                  isClearable
                  required
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.VAT_ZONE.writable
                  }
                />
              </Col>
            )}
            <input
              type='hidden'
              {...methods.register(`billingInformation._level`, {
                setValueAs: (value) => Number(value),
              })}
              value={watchedLevel}
            />
            {FormPrivileges.DELIVERY_FORM.fields.BILLING_CODE.visible && (
              <Col sm={4}>
                <TextField
                  name='billingInformation.billingCode'
                  label={
                    <Trans i18nKey='app.entity.form.billing_information.billing_code' />
                  }
                  required={watchedLevel === CUSTOMER_LEVEL.N1}
                  readOnly={
                    !FormPrivileges.DELIVERY_FORM.fields.BILLING_CODE.writable
                  }
                />
              </Col>
            )}
          </Row>
        </div>
      </Card.Body>
    </Card>
  );
};

export default DeliveryBillingForm;
